import { useCallback, useEffect, useRef } from 'react'
import { closeToast, showToast } from '../ui/toast'
import { addToast, newestError, typingIn, type Toast } from './toastCalc'

/**
 * The Reports toasts on HeroUI's region. A refusal stays until closed: Escape closes
 * the newest, and its close button takes focus when it arrives unless somebody is typing.
 */
export function useReportToasts(): (text: string, kind?: 'error') => void {
  const seq = useRef(0)
  const errors = useRef<Toast[]>([])
  const keys = useRef(new Map<number, string>())

  useEffect(() => {
    const open = keys.current
    const key = (e: KeyboardEvent) => {
      const newest = newestError(errors.current)
      // An open menu or popover closes first; then the newest refusal.
      if (!newest || e.key !== 'Escape' || e.defaultPrevented || document.querySelector('.insight-pop')) return
      e.preventDefault()
      e.stopPropagation()
      closeToast(open.get(newest.id) ?? '')
    }
    addEventListener('keydown', key, true)
    return () => {
      removeEventListener('keydown', key, true)
      // Refusals belong to Reports: leaving it closes them.
      for (const k of open.values()) closeToast(k)
    }
  }, [])

  return useCallback((text, kind) => {
    if (kind !== 'error') {
      showToast(text)
      return
    }
    const id = ++seq.current
    const next = addToast(errors.current, { id, text, kind })
    for (const t of errors.current) if (!next.includes(t)) closeToast(keys.current.get(t.id) ?? '')
    errors.current = next
    const forget = () => {
      errors.current = errors.current.filter((t) => t.id !== id)
      keys.current.delete(id)
    }
    keys.current.set(id, showToast(text, 'danger', 0, forget))
    requestAnimationFrame(() => {
      if (typingIn(document.activeElement as HTMLElement | null)) return
      document
        .querySelector<HTMLElement>('[data-slot="toast-region"] [data-slot="toast"][data-frontmost] [data-slot="toast-close"]')
        ?.focus({ preventScroll: true })
    })
  }, [])
}
